import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { apiClient } from "../../../shared/api/AllApi";
import { fetchSystemConfig, readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import { formatOrderNo } from "../../../shared/lib/documentNumbers";
import { Spin, Button, Table, Tag } from "antd";
import "../../../shared/styles/InfoOrder.css";
import { useTranslation } from "react-i18next";

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
const InfoOrder = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [systemSettings, setSystemSettings] = useState(() => readCachedSystemConfig());

  useEffect(() => {
    let cancelled = false;
    fetchSystemConfig()
      .then((config) => {
        if (!cancelled) setSystemSettings(config);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  const infoRowStyle = {
    margin: 0,
    fontSize: 16,
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: "6px",
    direction: "rtl",
    width: "100%",
  };
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setLoadError(false);
    apiClient
      .get(`/orders/${id}`, { signal: controller.signal })
      .then((response) => {
        setOrderDetails(response.data);
        setLoadError(false);
      })
      .catch((error) => {
        if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") return;
        console.error("Error fetching order details:", error);
        setOrderDetails(null);
        setLoadError(true);
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [id]);

  const columns = [
    {
      title: <b>#</b>,
      key: "rowNum",
      width: 44,
      align: "center",
      render: (_, __, index) => index + 1,
    },
    {
      title: <b>{t("details", "Details")}</b>,
      dataIndex: "description",
      key: "description",
      render: (text) => <span>{text || <b>—</b>}</span>,
    },
    {
      title: <b>{t("quantityWeight", "Qty / kg")}</b>,
      key: "qty",
      align: "center",
      width: 120,
      render: (_, row) => {
        const parts = [];
        if (row.rawQty > 0) parts.push(<span key="q">{row.rawQty}</span>);
        if (row.rawWeight > 0) parts.push(<span key="w" style={{ color: "#16a34a", fontWeight: 700 }}>{row.rawWeight} {t("kg", "kg")}</span>);
        if (!parts.length) return <span>—</span>;
        return <span style={{ display: "inline-flex", gap: 6, flexWrap: "wrap", justifyContent: "center" }}>{parts}</span>;
      },
    },
    {
      title: <b>{t("unitPrice", "Unit price")}</b>,
      dataIndex: "singlePrice",
      key: "singlePrice",
      align: "center",
      width: 130,
      render: (text) => <span>{text}</span>,
    },
    {
      title: <b>{t("subtotal", "Subtotal")}</b>,
      dataIndex: "subTotal",
      key: "subTotal",
      align: "center",
      width: 140,
      render: (text) => <b>{text}</b>,
    },
  ];
  const data = useMemo(() => {
    const mainCode = (systemSettings || readCachedSystemConfig())?.currency_code || "IQD";
    return (
      orderDetails?.order_products?.map((item, index) => {
        const useSub =
          item.sub_qty && item.sub_qty > 0 && item.sub_price && item.sub_price > 0;

        const formatNumber = (number) => {
          // Round to 2 decimal places and remove trailing zeros
          const rounded = Math.round(number * 100) / 100;
          return Number(rounded).toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
          });
        };

        const rawWeight = parseFloat(item.weight) || 0;
        const rawQty = parseFloat(item.quantity) || 0;
        const isWeight = rawWeight > 0;
        const quantity = isWeight ? rawWeight : rawQty;
        // Always use single_price from the order item
        const singlePrice = `${mainCode} ${formatNumber(item.single_price)} `;

        const unitSubName =
          !item.sub_qty || item.sub_qty === 0
            ? t("notAvailable", "N/A")
            : item.product?.sub_unit?.name
              ? (() => {
                try {
                  const subUnitObj = JSON.parse(item.product.sub_unit.name);
                  return (
                    subUnitObj[i18n.language] ||
                    subUnitObj.en ||
                    Object.values(subUnitObj).find(Boolean) ||
                    t("notAvailable", "N/A")
                  );
                } catch (error) {
                  console.error("Error parsing sub-unit name:", error);
                  return t("notAvailable", "N/A");
                }
              })()
              : t("notAvailable", "N/A");
        const ratio =
          !item.sub_qty || item.sub_qty === 0
            ? "-"
            : item.product?.sub_unit_ratio;
        const totalSubQty =
          !item.sub_qty || item.sub_qty === 0 ? "-" : item.sub_qty;
        const priceSubUnit =
          !item.sub_qty || item.sub_qty === 0
            ? "-"
            : item.sub_price != null
              ? `${mainCode} ${formatNumber(item.sub_price)}`
              : t("notAvailable", "N/A");

        // Use the total_price directly from the order item (saved in database)
        // instead of recalculating to avoid floating-point precision errors
        const subTotalValue = item.total_price || item.subtotal ||
          (useSub && item.sub_qty != null && item.sub_price != null
            ? item.sub_price * item.sub_qty
            : item.single_price * item.quantity);

        const subTotal = `${mainCode} ${formatNumber(subTotalValue)}`;
        return {
          key: index,
          description: (() => {
            try {
              if (item.product?.name) {
                const nameObj = JSON.parse(item.product.name);
                // Try current language, fallback to any available, fallback to en, fallback to value itself
                return nameObj[i18n.language] || nameObj.en || Object.values(nameObj).find(Boolean) || t("noProductAvailable", "No product available");
              }
            } catch (e) {
              // If parsing fails, fallback to raw value
              return item.product?.name || t("noProductAvailable", "No product available");
            }
            return t("noProductAvailable", "No product available");
          })(),
          quantity: `${quantity}`,
          isWeight,
          rawWeight,
          rawQty,
          unitSub: unitSubName,
          ratio: ratio,
          totalSubQty: totalSubQty,
          priceSubUnit: priceSubUnit,
          singlePrice: singlePrice,
          subTotal: subTotal,
        };
      }) || []
    );
  }, [orderDetails, i18n.language, t, systemSettings]);

  if (loading) {
    return (
      <div>
        <Spin tip={t("loading", "Loading...")}>
          <div style={{ width: "100%", height: "100vh" }} />
        </Spin>
      </div>
    );
  }

  if (!orderDetails || loadError) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "40vh", color: "#64748b", fontWeight: 600 }}>
        {t("noDataFound", "Failed to load order")}
      </div>
    );
  }

  const cfg = systemSettings || readCachedSystemConfig() || {};
  const mainCurrencyCode = cfg.currency_code || "IQD";
  const secondaryCurrencyCode = cfg.secondary_currency_code || "";

  const companyTitle =
    (cfg.invoice_company_name || "").trim() ||
    (cfg.site_name || "").trim() ||
    t("velanFactoryName", "IQ Price");
  const tagline = (cfg.invoice_tagline || "").trim();
  const headerLogoSrc = cfg.invoice_logo_url || cfg.sidebar_logo_url || "";
  const stampSrc = cfg.invoice_stamp_image_url || "";
  const watermarkEnabled = cfg.invoice_watermark_enabled !== false && Boolean(headerLogoSrc);
  const watermarkOpacity =
    typeof cfg.invoice_watermark_opacity === "number" ? cfg.invoice_watermark_opacity : 0.07;

  const displayEmail = (cfg.invoice_email || "").trim();
  const displayWeb = (cfg.invoice_website || "").trim();
  const phoneLines = (cfg.invoice_phones || "").trim()
    ? cfg.invoice_phones.split(/\r?\n/).map((s) => s.trim()).filter(Boolean)
    : [];
  const addressText = (cfg.invoice_address || "").trim();
  const footerNote = (cfg.invoice_footer_note || "").trim();

  return (
    <>
      <div className="no-print" style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: "#fff", borderBottom: "1px solid #e5e7eb",
        padding: "12px 24px", marginBottom: 16, gap: 16,
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      }}>
        <div style={{ display: "flex", gap: 24, alignItems: "center", flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>{t("order", "Order")}</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>
              {orderDetails.document_no || formatOrderNo(orderDetails.id, orderDetails.created_at)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>{t("customer", "Customer")}</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#1e293b" }}>{orderDetails.customer?.name || "—"}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>{t("date", "Date")}</div>
            <div style={{ fontSize: 13, color: "#475569" }}>{dayjs(orderDetails.created_at).format("DD/MM/YYYY HH:mm")}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>{t("total", "Total")}</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#2563eb" }}>{mainCurrencyCode} {Number(orderDetails.total_price).toLocaleString()}</div>
          </div>
          {!(orderDetails.is_taxed === false || orderDetails.is_taxed === 0) && Number(orderDetails.commission_amount) > 0 && (
            <div>
              <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>{t("posTerminalCommission", "Commission")}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#d97706" }}>+ {mainCurrencyCode} {Number(orderDetails.commission_amount).toLocaleString()}</div>
            </div>
          )}
          <div>
            <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>{t("paid", "Paid")}</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#16a34a" }}>{mainCurrencyCode} {Number(orderDetails.amount_paid).toLocaleString()}</div>
          </div>
          {orderDetails.outstanding_balance > 0 && (
            <div>
              <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>{t("outstanding", "Outstanding")}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#dc2626" }}>{mainCurrencyCode} {Number(orderDetails.outstanding_balance).toLocaleString()}</div>
            </div>
          )}
          {orderDetails.source === "pos" && (
            <Tag color="blue" style={{ marginTop: 8 }}>{t("sourcePos", "POS")}</Tag>
          )}
        </div>
        <Button
          type="primary"
          icon={<span style={{ fontSize: 16 }}>🖨️</span>}
          onClick={() => window.print()}
          style={{ height: 44, paddingInline: 28, fontSize: 15, fontWeight: 700, borderRadius: 10 }}
        >
          {t("print", "Print")}
        </Button>
      </div>

      <div style={{ textAlign: "center", marginTop: "0" }}>
        <div
          id="delivery-note"

          className="delivery-note-screen"
          style={{
            width: "210mm",
            margin: "auto",
            padding: "5px",
            boxSizing: "border-box",
            position: "relative",
          }}
        >
          {watermarkEnabled && (
            <img
              src={headerLogoSrc}
              alt={t("companyLogo", "Company Logo")}
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%) rotate(20deg)",
                opacity: watermarkOpacity,
                width: "70%",
                zIndex: 0,
                pointerEvents: "none",
                userSelect: "none",
              }}
              className="print-watermark"
            />
          )}
          <div
            style={{
              display: "flex",
              flexDirection: "row-reverse",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "10px",
            }}
          >
            <div style={{ textAlign: "right" }}>
              <h3 style={{ margin: 0, marginRight: 50, fontSize: 35 }}>
                <b>{companyTitle}</b>
              </h3>
              {tagline ? (
                <p style={{ margin: "4px 50px 0 0", fontSize: 14, fontWeight: 600 }}>
                  {tagline}
                </p>
              ) : null}
            </div>

            <div style={{ textAlign: "left" }}>
              {headerLogoSrc ? (
                <img
                  src={headerLogoSrc}
                  alt=""
                  style={{ height: 80, width: 250, marginLeft: 80, objectFit: "contain" }}
                />
              ) : null}
            </div>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "8px 24px",
              marginBottom: "20px",
              textAlign: "right",
              alignItems: "start",
            }}
          >
            <p style={infoRowStyle} dir="rtl">
              <b>{t("orderDate", "Order date")}</b>
              <b>:</b>
              <b dir="auto" style={{ unicodeBidi: "isolate" }}>
                {dayjs(orderDetails.created_at).format("DD/MM/YYYY")} {t("at", "at")} {dayjs(orderDetails.created_at).format("hh:mm A")}
              </b>
              {orderDetails.source === "pos" && (
                <Tag color="blue" style={{ marginRight: 8 }}>{t("sourcePos", "POS")}</Tag>
              )}
            </p>
            <p style={infoRowStyle} dir="rtl">
              <b>{t("customer", "Customer")}</b>
              <b>:</b>
              <b dir="auto" style={{ unicodeBidi: "isolate" }}>
                {orderDetails.customer?.name || ""}
              </b>
            </p>
            <p style={infoRowStyle} dir="rtl">
              <b>{t("phoneNumber", "Phone number")}</b>
              <b>:</b>
              <b dir="ltr" style={{ unicodeBidi: "isolate" }}>
                {orderDetails.customer?.phone_number || ""}
              </b>
            </p>
            {orderDetails.accountant &&
              typeof orderDetails.accountant === "string" &&
              orderDetails.accountant !== "" ? (
              <p style={infoRowStyle} dir="rtl">
                <b>{t("accountant", "Accountant")}</b>
                <b>:</b>
                <b dir="auto" style={{ unicodeBidi: "isolate" }}>
                  {orderDetails.accountant}
                </b>
              </p>
            ) : orderDetails.accountant && orderDetails.accountant.name ? (
              <p style={infoRowStyle} dir="rtl">
                <b>{t("accountant", "Accountant")}</b>
                <b>:</b>
                <b dir="auto" style={{ unicodeBidi: "isolate" }}>
                  {orderDetails.accountant.name}
                </b>
              </p>
            ) : orderDetails.accountant_id &&
              orderDetails.accountant_id !== "" ? (
              <p style={infoRowStyle} dir="rtl">
                <b>{t("accountant", "Accountant")}</b>
                <b>:</b>
                <b dir="auto" style={{ unicodeBidi: "isolate" }}>
                  {orderDetails.accountant_id}
                </b>
              </p>
            ) : (
              <p style={infoRowStyle} />
            )}
          </div>
          <Table
            columns={columns.map((col) => ({
              ...col,
              onCell: (record, rowIndex) => ({
                style: {
                  padding: "0px 8px",
                  fontSize: "12px",
                },
              }),
            }))}
            dataSource={data}
            pagination={false}
            className="custom-table"
            style={{
              marginBottom: 0,
              padding: 0,
              textAlign: "center",
            }}
            bordered
          />
            <div
            id="order-summary"
            className="order-summary-block"
            style={{
              marginLeft: 10, marginRight: 10, paddingBottom: 20,
              display: "flex", justifyContent: "space-between", alignItems: "center",
            }}
          >
            {stampSrc ? (
              <img src={stampSrc} alt="" style={{ height: 70, width: 70, marginLeft: 50, objectFit: "contain" }} />
            ) : <div style={{ width: 70, marginLeft: 50 }} />}
            <div style={{ textAlign: "right" }}>
              {!(orderDetails.is_taxed === false || orderDetails.is_taxed === 0) && Number(orderDetails.commission_amount) > 0 && (
                <>
                  <p style={{ margin: "4px 0", fontSize: 18, color: "#64748b" }}>
                    <b>{t("posTerminalSubtotal", "Subtotal")} : {mainCurrencyCode} {(Number(orderDetails.total_price) - Number(orderDetails.commission_amount)).toLocaleString()}</b>
                  </p>
                  <p style={{ margin: "4px 0", fontSize: 18, color: "#d97706" }}>
                    <b>{t("posTerminalCommission", "Commission")} : + {mainCurrencyCode} {Number(orderDetails.commission_amount).toLocaleString()}</b>
                  </p>
                </>
              )}
              <p style={{ margin: "4px 0", fontSize: 22 }}>
                <b>{t("totalLabel", "Total")} : {mainCurrencyCode} {Number(orderDetails.total_price).toLocaleString()}</b>
              </p>
              <p style={{ margin: "4px 0", fontSize: 20, color: "#16a34a" }}>
                <b>{t("totalPaidLabel", "Total Paid")} : {mainCurrencyCode} {Number(orderDetails.amount_paid).toLocaleString()}</b>
              </p>
              {Number(orderDetails.outstanding_balance) > 0 && (
                <p style={{ margin: "4px 0", fontSize: 20, color: "#dc2626" }}>
                  <b>{t("unpaidAmountLabel", "Unpaid Amount")} : {mainCurrencyCode} {Number(orderDetails.outstanding_balance).toLocaleString()}</b>
                </p>
              )}
            </div>
          </div>
          {footerNote ? (
            <div
              style={{
                textAlign: "center",
                padding: "8px 16px",
                fontSize: 13,
                whiteSpace: "pre-wrap",
              }}
            >
              {footerNote}
            </div>
          ) : null}
          <div
            id="print-footer"
            className="print-footer-block"
            style={{
              display: "flex",
              justifyContent: "space-between",
              left: "10px",
              right: "10px",
              padding: "0 10px",
            }}
          >
            <div>
              <p style={{ margin: 0, marginLeft: 0, fontSize: 14 }}>
                {displayEmail ? <b>{t("email", "Email")}:{displayEmail}</b> : null}
                {displayEmail && displayWeb ? <br /> : null}
                {displayWeb ? <b>{t("web", "Web")}:{displayWeb}</b> : null}
              </p>
            </div>
            <div>
              {phoneLines.map((line, idx) => (
                <p key={idx} style={{ margin: 0, fontSize: 14 }}>
                  <b>{line}</b>
                </p>
              ))}
            </div>
            <div>
              <p style={{ margin: 0, fontSize: 14, fontWeight: "bold", whiteSpace: "pre-wrap" }}>
                <b>{addressText}</b>
              </p>
            </div>
          </div>
        </div>

        <style>{`@media print {
          html, body {
            width: 100%;
            height: 100%;
            margin: 0;
            padding: 0;
          }

          @page {
            size: A4;
            margin: 6mm;
          }

          body * {
            visibility: hidden;
          }

          #delivery-note, #delivery-note * {
            visibility: visible;
          }

          .no-print {
            display: none !important;
          }

          /* Hide sidebar and header when printing - layout is full width */
          .app-sidebar,
          .ant-layout-sider,
          .app-header {
            display: none !important;
            visibility: hidden !important;
          }

          .main-layout {
            margin-left: 0 !important;
            width: 100% !important;
          }

          .app-content {
            width: 100% !important;
            padding: 0 !important;
          }

          /* Delivery note: flex column + min page height so totals sit at bottom when content is short */
          #delivery-note {
            position: relative !important;
            top: auto !important;
            left: auto !important;
            width: 100% !important;
            max-width: 198mm !important;
            display: flex !important;
            flex-direction: column !important;
            min-height: calc(297mm - 12mm) !important;
            margin: 0 auto !important;
            padding: 4mm 5mm 22mm 5mm !important;
            box-sizing: border-box;
            font-size: 11pt !important;
            color: black;
          }

          #delivery-note h3 {
            font-size: 15pt !important;
            margin: 0 0 1.5mm 0 !important;
          }

          /* Logos bigger and more visible */
          #delivery-note .header-logo img,
          #delivery-note img[alt]:not(.print-watermark) {
            height: 58px !important;
            width: auto !important;
          }

          #delivery-note p {
            font-size: 10pt !important;
            margin: 0.8mm 0 !important;
          }

          /* Table: 5% smaller; remove fake row / extra border below table */
          #delivery-note .custom-table,
          #delivery-note .ant-table {
            width: 100% !important;
            margin-bottom: 3mm !important;
            font-size: 9pt !important;
            page-break-inside: auto !important;
          }
          #delivery-note .ant-table-placeholder,
          #delivery-note .ant-table-tbody > tr.ant-table-placeholder {
            display: none !important;
          }
          #delivery-note .ant-table-wrapper,
          #delivery-note .ant-table-content {
            border-bottom: none !important;
          }

          #delivery-note .ant-table-thead {
            display: table-header-group !important;
          }

          #delivery-note .ant-table-thead > tr > th {
            padding: 1.4mm 2.4mm !important;
            font-size: 9pt !important;
            font-weight: 700 !important;
            color: #000 !important;
            line-height: 1.2 !important;
            text-align: center !important;
            background: #fff !important;
            border-bottom: 1px solid #000 !important;
          }

          #delivery-note .ant-table-tbody > tr > td,
          #delivery-note .custom-table td,
          #delivery-note .custom-table th {
            padding: 1.4mm 2.4mm !important;
            font-size: 9pt !important;
            line-height: 1.2 !important;
            text-align: center !important;
          }

          #delivery-note .ant-table-tbody > tr {
            page-break-inside: avoid !important;
          }

          /* Totals: stick to bottom of first sheet when table is short; avoid splitting across pages */
          #order-summary {
            position: relative !important;
            bottom: auto !important;
            left: auto !important;
            right: auto !important;
            margin-top: auto !important;
            margin-bottom: 3mm !important;
            padding-bottom: 0 !important;
            background: white !important;
            page-break-inside: avoid !important;
          }

          #order-summary p {
            font-size: 10pt !important;
          }

          #order-summary img {
            height: 52px !important;
            width: 52px !important;
          }

          /* Footer fixed at bottom of every printed page */
          #print-footer {
            position: fixed !important;
            bottom: 0 !important;
            left: 0 !important;
            right: 0 !important;
            margin-top: 0 !important;
            display: flex !important;
            justify-content: space-between !important;
            padding: 2.5mm 6mm !important;
            background: white !important;
            font-size: 9.5pt !important;
            box-shadow: 0 -1px 0 rgba(0,0,0,0.06);
          }

          #print-footer > div > p {
            margin: 0 !important;
            font-size: 9.5pt !important;
          }
        }

        @media screen {
          .delivery-note {
            width: 210mm;
            min-height: 297mm;
            margin: auto;
            padding: 5px;
            box-sizing: border-box;
          }
        }
      `}
        `}</style>
      </div>
    </>
  );
};
export default InfoOrder;
